import React, { useMemo } from "react";
import * as THREE from "three";
import { MeshTransmissionMaterial } from "@react-three/drei";
import { createLabelTexture } from "@/poc/labelTexture";

/*
 * Procedural Bordeaux-style (generic, non-trademarked) wine bottle.
 * Height ~3.15 units, body radius ~0.443, standing on y=0.
 */
function makeGlassGeometry() {
  const raw = [
    [0.001, 0.24], // punt apex
    [0.14, 0.17],
    [0.28, 0.055],
    [0.37, 0.02],
    [0.417, 0.035],
    [0.44, 0.11],
    [0.443, 0.3],
    [0.443, 0.9],
    [0.443, 1.4],
    [0.443, 1.78], // body top
    [0.432, 1.94],
    [0.395, 2.07],
    [0.315, 2.2],
    [0.215, 2.34],
    [0.152, 2.47],
    [0.13, 2.62],
    [0.126, 2.82],
    [0.126, 3.02],
    [0.14, 3.05],
    [0.143, 3.13],
    [0.12, 3.145],
    [0.085, 3.145], // lip inner
  ];
  const curve = new THREE.SplineCurve(raw.map(([x, y]) => new THREE.Vector2(x, y)));
  const pts = curve.getPoints(140);
  return new THREE.LatheGeometry(pts, 96);
}

function makeLiquidGeometry() {
  const raw = [
    [0.001, 0.27],
    [0.15, 0.19],
    [0.3, 0.09],
    [0.405, 0.09],
    [0.412, 0.4],
    [0.412, 1.72],
    [0.37, 1.98],
    [0.26, 2.2],
    [0.135, 2.42],
    [0.108, 2.55],
    [0.108, 2.62],
    [0.001, 2.62], // liquid surface in the neck
  ];
  const curve = new THREE.SplineCurve(raw.map(([x, y]) => new THREE.Vector2(x, y)));
  return new THREE.LatheGeometry(curve.getPoints(110), 64);
}

export const WineBottle = ({ isMobile = false }) => {
  const glassGeom = useMemo(() => makeGlassGeometry(), []);
  const liquidGeom = useMemo(() => makeLiquidGeometry(), []);
  const labelTexture = useMemo(() => createLabelTexture(), []);

  return (
    <group>
      {/* Glass shell — deep burgundy transmission glass */}
      <mesh geometry={glassGeom}>
        <MeshTransmissionMaterial
          samples={isMobile ? 4 : 8}
          resolution={isMobile ? 384 : 768}
          transmission={1}
          roughness={0.09}
          thickness={1.15}
          ior={1.5}
          chromaticAberration={0.015}
          anisotropicBlur={0.18}
          attenuationColor="#4B0F1E"
          attenuationDistance={0.45}
          clearcoat={1}
          clearcoatRoughness={0.06}
          envMapIntensity={1.1}
          color="#f2e8ea"
        />
      </mesh>

      {/* Wine inside — dark glossy mass seen through the glass */}
      <mesh geometry={liquidGeom}>
        <meshPhysicalMaterial
          color="#160309"
          roughness={0.12}
          metalness={0}
          clearcoat={0.6}
          clearcoatRoughness={0.2}
          envMapIntensity={0.35}
        />
      </mesh>

      {/* Ivory paper label wrapping the front of the body */}
      <mesh position={[0, 1.06, 0]}>
        <cylinderGeometry
          args={[0.452, 0.452, 0.84, 72, 1, true, -Math.PI * 0.475, Math.PI * 0.95]}
        />
        <meshStandardMaterial
          map={labelTexture}
          roughness={0.86}
          metalness={0}
          side={THREE.DoubleSide}
          envMapIntensity={0.25}
        />
      </mesh>

      {/* Near-black metallic capsule on the neck */}
      <mesh position={[0, 2.9, 0]}>
        <cylinderGeometry args={[0.148, 0.136, 0.58, 64]} />
        <meshStandardMaterial
          color="#0e0d0c"
          metalness={0.9}
          roughness={0.34}
          envMapIntensity={1.3}
        />
      </mesh>
      <mesh position={[0, 3.192, 0]}>
        <cylinderGeometry args={[0.148, 0.148, 0.012, 64]} />
        <meshStandardMaterial
          color="#12100e"
          metalness={0.88}
          roughness={0.4}
          envMapIntensity={1.2}
        />
      </mesh>

      {/* Restrained champagne-gold accent line at capsule base */}
      <mesh position={[0, 2.63, 0]}>
        <cylinderGeometry args={[0.139, 0.137, 0.022, 64]} />
        <meshStandardMaterial
          color="#C7A45D"
          metalness={1}
          roughness={0.24}
          envMapIntensity={1.4}
        />
      </mesh>
    </group>
  );
};
